import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { TrendingDown, TrendingUp, Minus, Plus, CheckCircle2, BedDouble, Clock } from "lucide-react";
import { fetchTarifExpress, applyTarifExpress } from "@/lib/api";
import { fmtPrice, withWatermark } from "@/lib/utils";

const DOWN = [-10, -20, -30];
const UP = [10, 20, 30];

export default function TarifExpressPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const suggested = [-30, -20, -10, 10, 20, 30].includes(Number(params.get("suggest"))) ? Number(params.get("suggest")) : null;
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pct, setPct] = useState(suggested ?? 0);
  const [rooms, setRooms] = useState(null);
  const [duration, setDuration] = useState("tomorrow_7h");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetchTarifExpress(token)
      .then((d) => { setInfo(d); setPct(suggested ?? (d.adjustment?.pct || 0)); setRooms(d.rooms_available); })
      .catch((e) => setError(e?.response?.data?.detail || "Lien invalide ou expire"));
  }, [token]);

  const preview = info ? Math.max(5000, Math.round((info.base_price * (1 + pct / 100)) / 100) * 100) : 0;

  const submit = async () => {
    setSaving(true);
    try {
      const r = await applyTarifExpress(token, { pct, rooms_available: rooms, duration });
      setDone(r);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur, reessayez");
    } finally {
      setSaving(false);
    }
  };

  if (error) return <Shell><div className="glass-card p-8 text-center text-red-600 font-bold" data-testid="tarif-express-error">{error}</div></Shell>;
  if (!info) return <Shell><div className="glass-card p-8 text-center">Chargement...</div></Shell>;
  if (info.access && !info.access.active) return (
    <Shell>
      <div className="glass-card p-8 text-center" data-testid="tarif-express-paywall">
        <div className="font-display text-2xl font-extrabold">{info.hotel.name}</div>
        <p className="mt-3 text-[var(--ink-soft)]">Votre periode d&apos;essai Tarif Express est terminee.</p>
        <p className="mt-2 font-semibold">Activez <b>Pro Hotel</b> ({fmtPrice(info.access.amount)} / mois) pour continuer a piloter vos prix par SMS, obtenir le badge Hotel partenaire et la mise en avant.</p>
        <a href={`/admin/hotels/${info.hotel.id}/disponibilites`} className="btn-primary mt-6 inline-flex w-full justify-center" data-testid="tarif-express-paywall-cta">Activer Pro Hotel</a>
      </div>
    </Shell>
  );
  if (done) return (
    <Shell>
      <div className="glass-card p-8 text-center" data-testid="tarif-express-done">
        <CheckCircle2 size={48} className="mx-auto text-emerald-600 mb-3" />
        <div className="font-display text-2xl font-extrabold">C&apos;est enregistre !</div>
        <p className="mt-3 text-[var(--ink-soft)]">{done.message}</p>
        <div className="mt-5 text-4xl font-extrabold text-orange-600" data-testid="tarif-express-new-price">{fmtPrice(done.current_price)}</div>
        <button onClick={() => setDone(null)} className="btn-secondary mt-6 w-full" data-testid="tarif-express-again">Modifier a nouveau</button>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="glass-card overflow-hidden" data-testid="tarif-express-page">
        {info.hotel.image && <img src={withWatermark(info.hotel.image)} alt={info.hotel.name} className="w-full h-36 object-cover" />}
        <div className="p-5">
          <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">Tarif Express · {info.date}{info.access?.mode === "trial" ? " · essai gratuit" : ""}</div>
          <h1 className="font-display text-2xl font-extrabold mt-1" data-testid="tarif-express-hotel">{info.hotel.name}</h1>
          {suggested !== null && (
            <div className="mt-3 rounded-2xl bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="tarif-express-suggestion">
              <div className="font-bold text-emerald-800">Suggestion Fodindin : {suggested > 0 ? "+" : ""}{suggested} % → {fmtPrice(Math.max(5000, Math.round((info.base_price * (1 + suggested / 100)) / 100) * 100))}</div>
              <div className="text-emerald-700 text-xs mt-0.5">Pré-sélectionnée ci-dessous — vous pouvez choisir un autre palier avant de valider.</div>
            </div>
          )}
          <div className="mt-4 flex items-end justify-between">
            <div>
              <div className="text-xs text-[var(--ink-soft)]">Prix de base</div>
              <div className="font-bold" data-testid="tarif-express-base">{fmtPrice(info.base_price)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-[var(--ink-soft)]">Nouveau prix</div>
              <div className="text-3xl font-extrabold text-orange-600" data-testid="tarif-express-preview">{fmtPrice(preview)}</div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2">
            <PctGroup icon={TrendingDown} label="Baisser" values={DOWN} pct={pct} setPct={setPct} tone="emerald" />
            <button type="button" onClick={() => setPct(0)} className={`rounded-2xl border-2 py-5 font-extrabold text-lg transition-colors ${pct === 0 ? "border-orange-500 bg-orange-50 text-orange-700" : "border-[var(--line)] bg-white"}`} data-testid="pct-0">
              Normal
            </button>
            <PctGroup icon={TrendingUp} label="Augmenter" values={UP} pct={pct} setPct={setPct} tone="rose" />
          </div>

          <div className="mt-6 flex items-center justify-between rounded-2xl border border-[var(--line)] p-3">
            <div className="flex items-center gap-2 font-bold"><BedDouble size={18} className="text-orange-600" /> Chambres libres</div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setRooms((r) => Math.max(0, (r ?? 0) - 1))} className="w-10 h-10 rounded-full border-2 border-[var(--line)] inline-flex items-center justify-center" data-testid="rooms-minus"><Minus size={16} /></button>
              <span className="text-2xl font-extrabold w-8 text-center" data-testid="rooms-value">{rooms ?? 0}</span>
              <button type="button" onClick={() => setRooms((r) => Math.min(100, (r ?? 0) + 1))} className="w-10 h-10 rounded-full border-2 border-[var(--line)] inline-flex items-center justify-center" data-testid="rooms-plus"><Plus size={16} /></button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <button type="button" onClick={() => setDuration("tomorrow_7h")} className={`rounded-xl border-2 py-2 font-semibold inline-flex items-center justify-center gap-1 ${duration === "tomorrow_7h" ? "border-orange-500 bg-orange-50" : "border-[var(--line)]"}`} data-testid="duration-tomorrow"><Clock size={14} /> Jusqu&apos;a demain 7h</button>
            <button type="button" onClick={() => setDuration("until_changed")} className={`rounded-xl border-2 py-2 font-semibold ${duration === "until_changed" ? "border-orange-500 bg-orange-50" : "border-[var(--line)]"}`} data-testid="duration-until">Jusqu&apos;a nouvel ordre</button>
          </div>

          <button onClick={submit} disabled={saving} className="btn-primary w-full mt-6 !py-4 !text-lg" data-testid="tarif-express-submit">
            {saving ? "Enregistrement..." : suggested !== null && pct === suggested ? `Confirmer ${pct < 0 ? "la baisse" : "la hausse"} à ${fmtPrice(preview)}` : "Valider"}
          </button>
          <p className="mt-3 text-[11px] text-center text-[var(--ink-soft)]">Lien securise valable jusqu&apos;au {new Date(info.expires_at).toLocaleString("fr-FR")}. Aucun mot de passe requis.</p>
        </div>
      </div>
    </Shell>
  );
}

function PctGroup({ icon: Icon, label, values, pct, setPct, tone }) {
  const active = values.includes(pct);
  const cls = tone === "emerald" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-rose-500 bg-rose-50 text-rose-700";
  return (
    <div className={`rounded-2xl border-2 p-2 flex flex-col gap-1 ${active ? cls : "border-[var(--line)] bg-white"}`} data-testid={`group-${label.toLowerCase()}`}>
      <div className="inline-flex items-center justify-center gap-1 font-extrabold"><Icon size={16} /> {label}</div>
      {values.map((v) => (
        <button key={v} type="button" onClick={() => setPct(v)} className={`rounded-lg py-1.5 text-sm font-bold ${pct === v ? "bg-[var(--ink)] text-white" : "bg-white/70"}`} data-testid={`pct-${v}`}>
          {v > 0 ? `+${v}` : v}%
        </button>
      ))}
    </div>
  );
}

function Shell({ children }) {
  return <main className="max-w-md mx-auto px-4 py-6" data-testid="tarif-express-shell">{children}</main>;
}
